import React, { useCallback, useState } from 'react';
import { Form, Formik } from 'formik';
import { ButtonTW } from '../../../../../tailwindComponents/buttonTW/ButtonTW';
import {
  useCreateTransferDeedMutation,
  useGetTransferDeedQuery,
  useUpdateTransferDeedMutation,
} from '../../../../../API/order/title/TransferDeedAPI';
import { UUID } from '../../../../../types/shared/UUID';
import { useParams } from 'react-router-dom';
import { VestingReasonSelect } from './VestingReasonSelect';
import { ICreateUpdateTransferDeedRequest } from '../../../../../types/title/ICreateUpdateTransferDeedRequest';
import { checkForMutationSuccess } from '../../../../../API/checkForMutationSuccess';
import { useHasPermissions } from '../../../../accessControl/hooks/useHasPermissions';
import { ePermissions } from '../../../../../types/administration/userAdministration/ePermissions';

export const VestingForm: React.FC = () => {
  const [isVestingDropdownDisabled, setIsVestingDropdownDisabled] = useState<boolean>(true);
  const { transferDeed, onSaveClick } = useGetTransferDeedAndSaveClick(setIsVestingDropdownDisabled);
  const canEditVesting = useHasPermissions([ePermissions.TransferDeedEdit]);

  return (
    <div className={'flex gap-4'}>
      <Formik
        initialValues={{
          vestingId: transferDeed?.vesting?.id,
        }}
        enableReinitialize
        onSubmit={onSaveClick}
      >
        <Form>
          <div className="flex flex-col">
            <VestingReasonSelect isVestingDropdownDisabled={isVestingDropdownDisabled} />
            <div className={'space-x-1'}>
              <ButtonTW
                secondary
                disabled={!canEditVesting}
                onClick={() => setIsVestingDropdownDisabled((prev) => !prev)}
              >
                {isVestingDropdownDisabled ? 'Edit' : 'Cancel'}
              </ButtonTW>
              {!isVestingDropdownDisabled && <ButtonTW type="submit">Save</ButtonTW>}
            </div>
          </div>
        </Form>
      </Formik>
    </div>
  );
};

const useGetTransferDeedAndSaveClick = (
  setIsVestingDropdownDisabled: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const { orderId } = useParams<{ orderId: UUID }>();

  const [createTransferDeedMutation] = useCreateTransferDeedMutation();
  const [updateTransferDeedMutation] = useUpdateTransferDeedMutation();
  const { data: transferDeed } = useGetTransferDeedQuery({ orderId });

  const onSaveClick = useCallback(
    async (formData: ICreateUpdateTransferDeedRequest) => {
      if (transferDeed) {
        const result = await updateTransferDeedMutation({ id: transferDeed.id, orderId, data: formData });
        checkForMutationSuccess(result, 'saving vesting reason');
      } else {
        const result = await createTransferDeedMutation({ orderId, data: formData });
        checkForMutationSuccess(result, 'creating vesting reason');
      }
      setIsVestingDropdownDisabled(true);
    },
    [createTransferDeedMutation, orderId, transferDeed, updateTransferDeedMutation, setIsVestingDropdownDisabled]
  );
  return { onSaveClick, transferDeed };
};
